/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  PencilSquareIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  MinusIcon,
  ArrowRightIcon,
  Square3Stack3DIcon,
  ChatBubbleOvalLeftIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  Bars2Icon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { safeStorage } from '../utils/safeStorage';

interface Point {
  x: number;
  y: number;
}

type Tool = 'select' | 'pen' | 'line' | 'arrow' | 'rectangle' | 'ellipse' | 'text' | 'eraser';

interface DrawingElement {
  id: string;
  type: Tool;
  points: Point[];
  color: string;
  lineWidth: number;
  text?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

const COLORS = [
  '#F97316', '#EF4444', '#22C55E', '#3B82F6', '#A855F7', '#FFFFFF',
];

const QuickNotesFloat: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 100 });
  const [size, setSize] = useState({ width: 400, height: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Canvas state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(COLORS[0]);
  const [lineWidth] = useState(2);
  const [elements, setElements] = useState<DrawingElement[]>([]);
  const [currentElement, setCurrentElement] = useState<DrawingElement | null>(null);
  const [undoStack, setUndoStack] = useState<DrawingElement[][]>([]);
  const [redoStack, setRedoStack] = useState<DrawingElement[][]>([]);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [draggedElement, setDraggedElement] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Load saved data
  useEffect(() => {
    const savedElements = safeStorage.getItem('quicknotes-float-elements');
    if (savedElements) {
      try {
        setElements(JSON.parse(savedElements));
      } catch (e) {
        console.error('Failed to load canvas data:', e);
      }
    }
  }, []);

  // Save elements
  useEffect(() => {
    safeStorage.setItem('quicknotes-float-elements', JSON.stringify(elements));
  }, [elements]);

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw dot grid
    ctx.fillStyle = '#2a2a4a';
    const gridSize = 15;
    for (let x = gridSize; x < canvas.width; x += gridSize) {
      for (let y = gridSize; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.arc(x, y, 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    [...elements, currentElement].filter(Boolean).forEach((element) => {
      if (!element) return;
      drawElement(ctx, element, element.id === selectedElement);
    });
  }, [elements, currentElement, selectedElement]);

  const drawElement = (ctx: CanvasRenderingContext2D, element: DrawingElement, isSelected: boolean) => {
    ctx.strokeStyle = element.type === 'eraser' ? '#1a1a2e' : element.color;
    ctx.fillStyle = element.color;
    ctx.lineWidth = element.type === 'eraser' ? element.lineWidth * 4 : element.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const offsetX = element.x || 0;
    const offsetY = element.y || 0;

    switch (element.type) {
      case 'pen':
      case 'eraser':
        if (element.points.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(element.points[0].x + offsetX, element.points[0].y + offsetY);
        for (let i = 1; i < element.points.length; i++) {
          ctx.lineTo(element.points[i].x + offsetX, element.points[i].y + offsetY);
        }
        ctx.stroke();
        break;

      case 'line':
        if (element.points.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(element.points[0].x + offsetX, element.points[0].y + offsetY);
        ctx.lineTo(element.points[element.points.length - 1].x + offsetX, element.points[element.points.length - 1].y + offsetY);
        ctx.stroke();
        break;

      case 'arrow':
        if (element.points.length < 2) return;
        const start = { x: element.points[0].x + offsetX, y: element.points[0].y + offsetY };
        const end = { x: element.points[element.points.length - 1].x + offsetX, y: element.points[element.points.length - 1].y + offsetY };
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLength = 10;
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - headLength * Math.cos(angle - Math.PI / 6), end.y - headLength * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - headLength * Math.cos(angle + Math.PI / 6), end.y - headLength * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
        break;

      case 'rectangle':
        if (element.points.length < 2) return;
        const rectStart = element.points[0];
        const rectEnd = element.points[element.points.length - 1];
        ctx.strokeRect(rectStart.x + offsetX, rectStart.y + offsetY, rectEnd.x - rectStart.x, rectEnd.y - rectStart.y);
        break;

      case 'ellipse':
        if (element.points.length < 2) return;
        const ellipseStart = element.points[0];
        const ellipseEnd = element.points[element.points.length - 1];
        const centerX = (ellipseStart.x + ellipseEnd.x) / 2 + offsetX;
        const centerY = (ellipseStart.y + ellipseEnd.y) / 2 + offsetY;
        const radiusX = Math.abs(ellipseEnd.x - ellipseStart.x) / 2;
        const radiusY = Math.abs(ellipseEnd.y - ellipseStart.y) / 2;
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.stroke();
        break;

      case 'text':
        if (element.text) {
          ctx.font = '14px "Segoe UI", system-ui, sans-serif';
          const textX = element.points[0].x + offsetX;
          const textY = element.points[0].y + offsetY;
          ctx.fillText(element.text, textX, textY);

          if (isSelected) {
            const metrics = ctx.measureText(element.text);
            ctx.strokeStyle = '#F97316';
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(textX - 4, textY - 16, metrics.width + 8, 22);
            ctx.setLineDash([]);
          }
        }
        break;
    }
  };

  useEffect(() => {
    if (!isOpen || isMinimized) return;
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      drawCanvas();
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [drawCanvas, isOpen, isMinimized, size]);

  useEffect(() => {
    if (isOpen && !isMinimized) drawCanvas();
  }, [drawCanvas, isOpen, isMinimized]);

  const getCanvasPoint = (e: React.MouseEvent | React.TouchEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const findElementAtPoint = (point: Point): DrawingElement | null => {
    for (let i = elements.length - 1; i >= 0; i--) {
      const el = elements[i];
      if (el.type === 'text' && el.text) {
        const offsetX = el.x || 0;
        const offsetY = el.y || 0;
        const x = el.points[0].x + offsetX;
        const y = el.points[0].y + offsetY;
        if (point.x >= x - 10 && point.x <= x + 100 && point.y >= y - 20 && point.y <= y + 10) {
          return el;
        }
      }
    }
    return null;
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const point = getCanvasPoint(e);

    if (tool === 'select') {
      const el = findElementAtPoint(point);
      if (el) {
        setSelectedElement(el.id);
        setDraggedElement({ id: el.id, offsetX: point.x - (el.x || 0), offsetY: point.y - (el.y || 0) });
      } else {
        setSelectedElement(null);
      }
      return;
    }

    if (tool === 'text') {
      const text = prompt('Enter text:');
      if (text) {
        const newElement: DrawingElement = {
          id: generateId(),
          type: 'text',
          points: [point],
          color,
          lineWidth,
          text,
          x: 0,
          y: 0,
        };
        setUndoStack([...undoStack, elements]);
        setRedoStack([]);
        setElements([...elements, newElement]);
      }
      return;
    }

    setIsDrawing(true);
    setStartPoint(point);
    setCurrentElement({
      id: generateId(),
      type: tool,
      points: [point],
      color,
      lineWidth,
    });
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const point = getCanvasPoint(e);

    if (draggedElement) {
      setElements(elements.map(el =>
        el.id === draggedElement.id
          ? { ...el, x: point.x - draggedElement.offsetX, y: point.y - draggedElement.offsetY }
          : el
      ));
      return;
    }

    if (!isDrawing || !currentElement || !startPoint) return;

    if (tool === 'pen' || tool === 'eraser') {
      setCurrentElement({ ...currentElement, points: [...currentElement.points, point] });
    } else {
      setCurrentElement({ ...currentElement, points: [startPoint, point] });
    }
  };

  const stopDrawing = () => {
    if (draggedElement) {
      setDraggedElement(null);
      return;
    }
    if (isDrawing && currentElement && currentElement.points.length > 1) {
      setUndoStack([...undoStack, elements]);
      setRedoStack([]);
      setElements([...elements, currentElement]);
    }
    setIsDrawing(false);
    setCurrentElement(null);
    setStartPoint(null);
  };

  const undo = () => {
    if (undoStack.length === 0) return;
    setRedoStack([...redoStack, elements]);
    setElements(undoStack[undoStack.length - 1]);
    setUndoStack(undoStack.slice(0, -1));
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    setUndoStack([...undoStack, elements]);
    setElements(redoStack[redoStack.length - 1]);
    setRedoStack(redoStack.slice(0, -1));
  };

  const clearCanvas = () => {
    if (elements.length === 0) return;
    setUndoStack([...undoStack, elements]);
    setRedoStack([]);
    setElements([]);
  };

  // Window dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - 50, e.clientY - dragOffset.y)),
      });
    }
    if (isResizing) {
      setSize({
        width: Math.max(300, e.clientX - position.x),
        height: Math.max(300, e.clientY - position.y),
      });
    }
  }, [isDragging, isResizing, dragOffset, position]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const tools: { id: Tool; icon: React.ReactNode; label: string }[] = [
    { id: 'select', icon: <Bars2Icon className="w-3.5 h-3.5" />, label: 'Select & Move' },
    { id: 'pen', icon: <PencilIcon className="w-3.5 h-3.5" />, label: 'Pen' },
    { id: 'line', icon: <MinusIcon className="w-3.5 h-3.5" />, label: 'Line' },
    { id: 'arrow', icon: <ArrowRightIcon className="w-3.5 h-3.5" />, label: 'Arrow' },
    { id: 'rectangle', icon: <Square3Stack3DIcon className="w-3.5 h-3.5" />, label: 'Rectangle' },
    { id: 'text', icon: <ChatBubbleOvalLeftIcon className="w-3.5 h-3.5" />, label: 'Text' },
  ];

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full shadow-lg shadow-primary-500/30 flex items-center justify-center text-white hover:scale-110 transition-transform"
          title="Quick Notes"
        >
          <PencilSquareIcon className="w-6 h-6" />
        </button>
      )}

      {/* Floating Panel */}
      {isOpen && (
        <div
          className="fixed z-50 bg-gray-900 rounded-xl border border-gray-700 shadow-2xl overflow-hidden flex flex-col"
          style={{
            left: position.x,
            top: position.y,
            width: isMinimized ? 200 : size.width,
            height: isMinimized ? 'auto' : size.height,
          }}
          onMouseDown={handleMouseDown}
        >
          {/* Header */}
          <div className="drag-handle flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700 cursor-move select-none">
            <div className="flex items-center gap-2">
              <PencilSquareIcon className="w-4 h-4 text-primary-500" />
              <span className="text-sm font-medium text-white">Quick Notes</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/quick-notes')}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                title="Open full page"
              >
                <ArrowsPointingOutIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                {isMinimized ? <ArrowsPointingOutIcon className="w-4 h-4" /> : <ArrowsPointingInIcon className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-red-400 rounded transition-colors"
                title="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-800/50 border-b border-gray-700 flex-wrap">
                <div className="flex items-center gap-0.5 bg-gray-900 rounded p-0.5">
                  {tools.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTool(t.id)}
                      className={`p-1.5 rounded transition-colors ${
                        tool === t.id ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
                      }`}
                      title={t.label}
                    >
                      {t.icon}
                    </button>
                  ))}
                </div>
                <div className="h-4 w-px bg-gray-700" />
                <div className="flex items-center gap-0.5">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`w-4 h-4 rounded-full border ${color === c ? 'border-white scale-125' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <div className="h-4 w-px bg-gray-700" />
                <button onClick={undo} disabled={undoStack.length === 0} className="p-1 text-gray-400 hover:text-white disabled:opacity-30" title="Undo">
                  <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                </button>
                <button onClick={redo} disabled={redoStack.length === 0} className="p-1 text-gray-400 hover:text-white disabled:opacity-30" title="Redo">
                  <ArrowUturnRightIcon className="w-3.5 h-3.5" />
                </button>
                <button onClick={clearCanvas} disabled={elements.length === 0} className="p-1 text-red-400 hover:text-red-300 disabled:opacity-30" title="Clear">
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Canvas */}
              <div ref={containerRef} className="flex-1 relative">
                <canvas
                  ref={canvasRef}
                  className={`absolute inset-0 ${tool === 'select' ? 'cursor-move' : tool === 'text' ? 'cursor-text' : 'cursor-crosshair'} touch-none`}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>

              {/* Resize Handle */}
              <div
                className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
                onMouseDown={(e) => { e.stopPropagation(); setIsResizing(true); }}
              >
                <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
                </svg>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default QuickNotesFloat;
